// src/components/auth/SocialProfileModal.tsx
import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Chip,
  Stack
} from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TwitchIcon from '@mui/icons-material/SportsEsports';
import FacebookIcon from '@mui/icons-material/Facebook';

export type SocialProfile = {
  platform: 'instagram' | 'youtube' | 'twitch' | 'facebook';
  handle: string;
  profile_url: string;
  follower_count?: string;
};

interface Props {
  open: boolean;
  onSubmit: (profile: SocialProfile) => void;
  onCancel: () => void;
}

const normalize = (s: string) => s.trim();

function detectPlatformAndHandle(inputRaw: string): SocialProfile | null {
  const input = normalize(inputRaw);
  if (!input) return null;

  // If starts with @, assume Instagram by default (most common)
  if (input.startsWith('@')) {
    const handle = input.slice(1);
    return {
      platform: 'instagram',
      handle,
      profile_url: `https://instagram.com/${handle}`,
    };
  }

  try {
    const url = new URL(input.includes('://') ? input : `https://${input}`);
    const host = url.hostname.toLowerCase();
    const path = url.pathname.replace(/\/$/, '');

    if (host.includes('instagram.com')) {
      const handle = path.split('/').filter(Boolean)[0] || '';
      if (!handle) return null;
      return { platform: 'instagram', handle, profile_url: `https://instagram.com/${handle}` };
    }
    if (host.includes('youtube.com')) {
      // support /@handle or /channel/ID
      const parts = path.split('/').filter(Boolean);
      if (!parts.length) return null;
      if (parts[0].startsWith('@')) {
        const handle = parts[0].slice(1);
        return { platform: 'youtube', handle, profile_url: `https://youtube.com/@${handle}` };
      }
      // Fallback to channel id as handle
      const handle = parts.join('/');
      return { platform: 'youtube', handle, profile_url: `https://youtube.com/${handle}` };
    }
    if (host.includes('twitch.tv')) {
      const handle = path.split('/').filter(Boolean)[0] || '';
      if (!handle) return null;
      return { platform: 'twitch', handle, profile_url: `https://twitch.tv/${handle}` };
    }
    if (host.includes('facebook.com')) {
      const handle = path.split('/').filter(Boolean)[0] || '';
      if (!handle) return null;
      return { platform: 'facebook', handle, profile_url: `https://facebook.com/${handle}` };
    }
  } catch {
    // Not a URL, fall through
  }

  // Plain text without @ — assume Instagram username
  if (/^[a-zA-Z0-9._]{2,}$/.test(input)) {
    return { platform: 'instagram', handle: input, profile_url: `https://instagram.com/${input}` };
  }

  return null;
}

const IconForPlatform: Record<SocialProfile['platform'], React.ReactNode> = {
  instagram: <InstagramIcon sx={{ color: '#E1306C' }} />,
  youtube: <YouTubeIcon sx={{ color: '#FF0000' }} />,
  twitch: <TwitchIcon sx={{ color: '#6441A5' }} />,
  facebook: <FacebookIcon sx={{ color: '#1877F2' }} />,
};

const SocialProfileModal: React.FC<Props> = ({ open, onSubmit, onCancel }) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');

  const parsed = useMemo(() => (value ? detectPlatformAndHandle(value) : null), [value]);

  const handleConfirm = () => {
    if (!parsed) {
      setError('Enter a valid Instagram, YouTube, Twitch, or Facebook profile link or @handle');
      return;
    }
    onSubmit(parsed);
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>Connect a public social profile</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          To finish setting up your account, add at least one public profile. This helps hosts evaluate requests.
        </Typography>

        <TextField
          fullWidth
          label="Profile link or @handle"
          placeholder="e.g. https://instagram.com/yourname or @yourname"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError('');
          }}
          error={!!error}
          helperText={error || (parsed ? `Detected: ${parsed.platform} • @${parsed.handle}` : 'Instagram, YouTube, Twitch, or Facebook')}
        />

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">Supported</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip icon={<InstagramIcon />} label="Instagram" size="small" />
            <Chip icon={<YouTubeIcon />} label="YouTube" size="small" />
            <Chip icon={<TwitchIcon />} label="Twitch" size="small" />
            <Chip icon={<FacebookIcon />} label="Facebook" size="small" />
          </Stack>
        </Box>

        {parsed && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              {IconForPlatform[parsed.platform]}
              <Typography variant="body2">@{parsed.handle}</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">{parsed.profile_url}</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!parsed}
          sx={{ bgcolor: '#2D2070', '&:hover': { bgcolor: '#1f1550' } }}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SocialProfileModal;
